import { IDisposable } from "monaco-editor-core";
import ReactDOM from "react-dom";

import { Size } from "../../Data/Geometry/Size";
import { Vec2 } from "../../Data/Geometry/Vec2";
import styles from "./Popup.module.scss";

export interface Popup extends IDisposable {
  content: HTMLElement;
  renderTitleBar: () => void;
}

export interface CreatePopupConfig {
  animationTransform: Vec2;
  position: Vec2;
  size: Size;
  titleBarClassName: string;
  renderTitleBar: () => React.FunctionComponentElement<any> | React.FunctionComponentElement<any>[];
}

declare namespace Popup {
  export interface Events {
    onExpand: () => void;
  }
}

export function createPopup(config: CreatePopupConfig, listener: Popup.Events): Popup {
  const cont: HTMLElement = document.createElement("div");
  cont.classList.add(styles.cont); //, "bp3-popover", "bp3-minimal");
  cont.style.width = "" + config.size.width + "px";
  cont.style.height = "" + config.size.height + "px";
  cont.style.left = "" + config.position.x + "px";
  cont.style.top = "" + config.position.y + "px";

  document.body.appendChild(cont);

  cont.animate(
    [
      {
        opacity: 0,
        transform: `translate3d(${config.animationTransform.x}px, ${config.animationTransform.y}px, 0)`
      },
      {
        opacity: 1,
        transform: "none"
      }
    ],
    {
      easing: "ease-out",
      duration: 100
    }
  );

  const titleBar = document.createElement("div");
  titleBar.classList.add(styles.titlebar, config.titleBarClassName);

  ReactDOM.render(config.renderTitleBar(), titleBar);

  cont.appendChild(titleBar);

  const contentCont = document.createElement("div");
  contentCont.classList.add(styles.content_cont, "monaco-editor-background");

  cont.appendChild(contentCont);

  const content = document.createElement("div");
  content.className = styles.content;
  contentCont.appendChild(content);

  const overlayTitlebar = document.createElement("div");
  const overlayContent = document.createElement("div");

  const hover = (): void => {
    overlayTitlebar.classList.add(styles.overlay_hover);
    overlayContent.classList.add(styles.overlay_hover);
  };

  const unhover = (): void => {
    overlayTitlebar.classList.remove(styles.overlay_hover);
    overlayContent.classList.remove(styles.overlay_hover);
  };

  const expand = (): void => {
    listener.onExpand();

    cont.removeChild(overlayTitlebar);
    cont.removeChild(overlayContent);

    titleBar.classList.add(styles.titlebar_expanded);

    let left = 300;
    let top = 100;
    const startLeft = left;
    const startTop = top;

    cont.style.left = `${left}px`;
    cont.style.top = `${top}px`;
    cont.style.width = "700px";
    cont.style.height = "300px";

    titleBar.addEventListener("mousedown", (e: MouseEvent) => {
      if (e.target === titleBar) {
        cont.style.transition = "none";
        console.log("MOUSEDOWN", e.clientX, e.clientY);
        const startX = e.clientX;
        const startY = e.clientY;
        const handleMousemove = (e: MouseEvent): void => {
          // console.log("MOUSEMOVE", e.clientX, e.clientY);
          left = startLeft + e.clientX - startX;
          top = startTop + e.clientY - startY;
          cont.style.left = `${left}px`;
          cont.style.top = `${top}px`;
        };
        document.addEventListener("mousemove", handleMousemove);
        document.addEventListener(
          "mouseup",
          () => {
            console.log("MOUSEUP");
            document.removeEventListener("mousemove", handleMousemove);
          },
          {
            once: true
          }
        );
      }
    });
  };

  const overlayRegisterEventListeners = (elem: HTMLElement): void => {
    elem.addEventListener("mouseenter", () => {
      hover();
    });
    elem.addEventListener("mouseleave", () => {
      unhover();
    });
    elem.addEventListener("click", () => {
      expand();
    });
  };

  overlayTitlebar.className = styles.overlay_titlebar;
  overlayRegisterEventListeners(overlayTitlebar);
  cont.appendChild(overlayTitlebar);

  overlayContent.className = styles.overlay_content_cont;
  overlayRegisterEventListeners(overlayContent);
  cont.appendChild(overlayContent);

  return {
    content: content,
    renderTitleBar: () => {
      ReactDOM.render(config.renderTitleBar(), titleBar);
    },
    dispose: () => {
      ReactDOM.unmountComponentAtNode(titleBar);
      document.body.removeChild(cont);
    }
  };
}
